import { isEmpty, sortBy } from "./nativeUtils";
import { FilterOptionEnum, ServiceEnum } from "../models/dataModel";
import config from "../config";
export function normalizeSearchTerm(searchTerm) {
    return searchTerm && !searchTerm.trim() ? "*" : searchTerm;
}
/**
 * Checks if a product filter has been set
 * @param filters list of filters to check
 */
export function hasProductBeenSelected(filters) {
    return !!filters.find((filter) => filter.field === FilterOptionEnum.PRODUCT);
}
/**
 * Checks if a version filter has been set
 * @param filters list of filters to check
 */
export function hasVersionBeenSelected(filters) {
    return !!filters.find((filter) => filter.field === FilterOptionEnum.VERSION);
}
/**
 * Checks if a given category facet has subcategories
 * @param category FacetValue
 */
export function hasSubcategories(category) {
    const categoryConfig = !!category && Object.values(config.services).find((service) => service.categoryName === category.value);
    return !!categoryConfig && categoryConfig.facetList !== undefined && !isEmpty(categoryConfig.facetList);
}
/**
 * Checks if a version facet option exists
 * @param facets list of facets to check
 * @param version name of the version
 */
export function checkIfVersionExists(facets, version) {
    const versionFacet = !!facets && !!facets.software_version && facets.software_version[0];
    return !!versionFacet && !!versionFacet.data.find((option) => option.value === version);
}
/**
 * Returns a sorted list of facet options with selected options at the top.
 * Adds selected options to the top even if they don't appear among the most popular options.
 * @param facetValue selected facet
 * @param options the top X options for facet, updated if user clicks 'show more'
 * @param filters list of all selected filters
 * @param allFacets list of all facets
 * @returns list of options, sorted by selected
 */
export function getSortedFacetOptions(facetValue, options, filters, allFacets) {
    const facet = !!allFacets[facetValue] && allFacets[facetValue][0];
    if (!facet) {
        return sortBy(options, (option) => !option.selected);
    }
    else {
        const optionsWithSelected = options;
        const allFacetValues = facet.data;
        if (allFacetValues.length === options.length) {
            return sortBy(options, (option) => !option.selected);
        }
        const filtersForFacet = filters.filter((filter) => filter.field === facetValue);
        !!filtersForFacet &&
            !!filtersForFacet[0] &&
            filtersForFacet[0].values.forEach((filterValue) => {
                const optionVisible = !!options.find((option) => {
                    return option.value === filterValue;
                });
                if (!optionVisible) {
                    const optionForFilter = allFacetValues.find((option) => {
                        return option.value === filterValue;
                    });
                    if (optionForFilter) {
                        optionsWithSelected.push({
                            ...optionForFilter,
                            selected: true,
                        });
                    }
                }
            });
        return sortBy(optionsWithSelected, (option) => !option.selected);
    }
}
/**
 * Resolves the search index based on environemnt, language and the service.
 * @param env the environment
 * @param language language key, already resolved for the service
 * @param service the service in which the client is rendered
 * @param searchAllIndices should search all indices?
 * @returns the search index
 */
export function resolveSearchIndex(env, language, service, searchAllIndices) {
    const validEnv = resolveEnvironment(env || "prod");
    if (searchAllIndices) {
        return (Object.keys(config.services)
            // Filter services that have content for the given language
            .filter((serviceKey) => {
            return !!resolveLanguage(language, serviceKey);
        })
            // Map service, env and language to search index string
            .map((serviceKey) => {
            return `${serviceKey}_${validEnv}-${language}`;
        })
            // Combine to a single string
            .join(","));
    }
    else {
        return resolveLanguage(language, service) ? `${service}_${validEnv}-${language}` : "";
    }
}
/**
 * Resolves the environment in short format based on given env string.
 * @param env environment, given to search client through anchor div
 * @returns the env string in short format
 */
function resolveEnvironment(env) {
    if (env === "local")
        return "local";
    else if (env === "dev" || env === "development")
        return "dev";
    else if (env === "stg" || env === "staging")
        return "stg";
    else if (env === "prod" || env === "production")
        return "prod";
    else
        return "prod";
}
/**
 * Resolves the service specific language used for indexing search content.
 * @param lang language key
 * @param service service to resolve language for
 * @returns language string or undefined if content for given language does not exists in the given service
 */
export function resolveLanguage(lang, service) {
    // Normalize lang input
    lang = lang.toLowerCase();
    // Default to 'thlp' service if none given
    service = service || ServiceEnum.TRIMBLE_HELP;
    const mappedLanguages = {
        // English variants
        en: { thlp: "en", down: "en" },
        "en-us": { thlp: "en", down: "en" },
        "en-gb": { thlp: "en-gb", down: undefined },
        // Danish
        "da-dk": { thlp: "da-dk", down: undefined },
        dk: { thlp: "da-dk", down: undefined },
        // German
        "de-de": { thlp: "de-de", down: undefined },
        de: { thlp: "de-de", down: undefined },
        // Spanish
        "es-es": { thlp: "es-es", down: undefined },
        "es-mx": { thlp: "es-mx", down: undefined },
        la: { thlp: "es-mx", down: undefined },
        es: { thlp: "es-es", down: undefined },
        // French
        "fr-fr": { thlp: "fr-fr", down: undefined },
        fr: { thlp: "fr-fr", down: undefined },
        // Italian
        "it-it": { thlp: "it-it", down: undefined },
        it: { thlp: "it-it", down: undefined },
        // Dutch
        "nl-nl": { thlp: "nl-nl", down: undefined },
        nl: { thlp: "nl-nl", down: undefined },
        // Norwegian
        "nb-no": { thlp: "nb-no", down: undefined },
        no: { thlp: "nb-no", down: undefined },
        // Polish
        "pl-pl": { thlp: "pl-pl", down: undefined },
        pl: { thlp: "pl-pl", down: undefined },
        // Portuguese
        "pt-br": { thlp: "pt-br", down: undefined },
        pt: { thlp: "pt-br", down: undefined },
        // Finnish
        fi: { thlp: "fi-fi", down: undefined },
        "fi-fi": { thlp: "fi-fi", down: undefined },
        // Swedish
        se: { thlp: "sv-se", down: undefined },
        sv: { thlp: "sv-se", down: undefined },
        "sv-se": { thlp: "sv-se", down: undefined },
        // Czech
        cs: { thlp: "cs-cz", down: undefined },
        cz: { thlp: "cs-cz", down: undefined },
        "cs-cz": { thlp: "cs-cz", down: undefined },
        // Russian
        ru: { thlp: "ru-ru", down: undefined },
        "ru-ru": { thlp: "ru-ru", down: undefined },
        // Japanese
        ja: { thlp: "ja-jp", down: undefined },
        jp: { thlp: "ja-jp", down: undefined },
        "ja-jp": { thlp: "ja-jp", down: undefined },
        // Simplified Chinese
        "zh-cn": { thlp: "zh-cn", down: undefined },
        "zh-hans": { thlp: "zh-cn", down: undefined },
        ch: { thlp: "zh-cn", down: undefined },
        cn: { thlp: "zh-cn", down: undefined },
        // Chinese, Taiwan
        "zh-tw": { thlp: "zh-tw", down: undefined },
        // Korean
        "ko-kr": { thlp: "ko-kr", down: undefined },
        kr: { thlp: "ko-kr", down: undefined },
        ko: { thlp: "ko-kr", down: undefined },
    };
    return mappedLanguages[lang] ? mappedLanguages[lang][service] : undefined;
}
/**
 * Resolves a query string from given list of filters
 * @param filters list of existing filters
 * @returns search query for the url
 */
export function resolveQueryString(filters) {
    let queryStr = "";
    filters.forEach((filter, i) => {
        queryStr =
            queryStr +
                `&filters[${i}][field]=${filter.field}` +
                filter.values
                    .map((filterValue, j) => `&filters[${i}][values][${j}]=${filterValue}`)
                    .join("") +
                `&filters[${i}][type]=${filter.type}`;
    });
    return queryStr;
}
